/**
 * StudioGuideOverlay — B-2026-0058, B-2026-0085, B-2026-0088
 *
 * Coach-facing Adaptive Guidance Overlay for the Coaching Studio.
 * Renders via Portal at z-[10000] — on top of ALL UI including canvas layers.
 *
 * Layout: delegates to GuideDrawerLayout (DesktopDrawer / MobilePortraitSheet /
 * MobileLandscapePill) — no more duplicated layout code here.
 *
 * Content reactivity (priority order):
 *   1. focusedField   — field-level micro-guidance (Wave 2)
 *   2. selectedTool   — tool-selected guidance (Wave 2)
 *   3. activePanel    — deep-settings guidance (Wave 1)
 *   4. activeSection  — scroll-spy visible panel (B-2026-0088 Phase A2)
 *   5. activePhase    — phase-selected guidance (Wave 1)
 *   6. null           — studio overview
 *
 * B-2026-0088 Phase A2: useAnchorSpy is called here to drive activeSection
 * from data-guide-section elements in RegionAnnotator.
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import { resolveStudioGuideContent } from '@/data/studioGuideContent';
import { GUIDE_DISMISSED_STUDIO_KEY } from '@/utils/constants';
import { GuideCardContent } from './GuideCardContent';
import { GuideDrawerLayout } from './GuideDrawerLayout';
import { resolveGuideBody } from '@/data/guideContentTypes';
import { useAnchorSpy } from '@/hooks/useAnchorSpy';
import { AnchorHighlight } from './AnchorHighlight';
import { useAnchorRect } from '@/hooks/useAnchorRect';

// ---------------------------------------------------------------------------
// Helper — resolve guide content, key, and context hint
// ---------------------------------------------------------------------------

import {
  FIELD_ID_TO_GUIDE_KEY,
  TOOL_TO_GUIDE_KEY,
  STUDIO_SECTION_TO_KEY,
} from '@/data/studioGuideContent';

function useStudioGuideKey(): string {
  const { focusedField, selectedTool, activePanel, activePhase, activeSection } = useAdaptiveGuide();

  if (focusedField) {
    const k = FIELD_ID_TO_GUIDE_KEY[focusedField];
    if (k) return k;
  }
  if (selectedTool) {
    const k = TOOL_TO_GUIDE_KEY[selectedTool];
    if (k) return k;
  }
  if (activePanel) {
    if (activePanel === 'scoring') return 'deep-settings-scoring-emphasis';
    if (activePanel === 'body-form') return 'deep-settings-body-form';
    if (activePanel === 'eom') return 'deep-settings-eom';
    return activePanel;
  }
  if (activeSection) {
    const k = STUDIO_SECTION_TO_KEY[activeSection];
    if (k) return k;
    return activeSection;
  }
  if (activePhase !== null) return 'phase-selected';
  return 'studio-overview';
}

function useStudioContent() {
  const { focusedField, selectedTool, activePanel, activePhase, activeSection } = useAdaptiveGuide();
  const entry = resolveStudioGuideContent(
    focusedField,
    selectedTool,
    activePanel,
    activePhase,
    activeSection,
  );
  const body = resolveGuideBody(entry, {});
  return { entry, body };
}

function useContextHint(): string {
  const { focusedField, selectedTool, activePanel, activePhase, activeSection } = useAdaptiveGuide();
  if (focusedField) return 'Showing guidance for the focused field.';
  if (selectedTool) return 'Showing guidance for the selected tool.';
  if (activePanel) return 'Showing guidance for the active settings panel.';
  if (activeSection) return 'Showing guidance for the visible panel.';
  if (activePhase !== null) return 'Showing guidance for the selected phase.';
  return 'Select a tool or phase to see contextual tips.';
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function StudioGuideOverlay() {
  const { isGuideOpen, setGuideOpen, report } = useAdaptiveGuide();
  const { entry, body } = useStudioContent();
  const guideKey = useStudioGuideKey();
  const contextHint = useContextHint();
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Phase A2: drive activeSection from data-guide-section elements in RegionAnnotator.
  // The spy reports which studio panel is currently most visible in the viewport.
  useAnchorSpy(report, 'data-guide-section');

  // Phase A3: anchor rect tracking
  const { rect: anchorRect, isOffscreen, isCovered } = useAnchorRect(
    guideKey,
    isGuideOpen,
  );

  // Measure the card panel rect for arrow source
  const [cardRect, setCardRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!isGuideOpen) { setCardRect(null); return; }
    const el = cardRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      setCardRect(el.getBoundingClientRect());
    });
    return () => cancelAnimationFrame(raf);
  }, [isGuideOpen, guideKey]);

  // Show arrow only when: anchor element exists, neither offscreen nor covered
  const showHighlight = isGuideOpen && anchorRect !== null && !isOffscreen && !isCovered;

  const handleDismiss = useCallback(() => {
    setGuideOpen(false);
    try {
      localStorage.setItem(GUIDE_DISMISSED_STUDIO_KEY, 'true');
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }, [setGuideOpen]);

  if (!isGuideOpen) return null;

  return (
    <>
      <GuideDrawerLayout
        isOpen={isGuideOpen}
        onDismiss={handleDismiss}
        heading={entry.heading}
        contextHint={contextHint}
        cardRef={cardRef}
      >
        <GuideCardContent body={body} isOffscreen={isOffscreen} />
      </GuideDrawerLayout>
      <AnchorHighlight
        show={showHighlight}
        anchorRect={anchorRect}
        cardRect={cardRect}
      />
    </>
  );
}

export default StudioGuideOverlay;
